
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useNotifications } from '@/hooks/notifications/useNotifications';
import { Bell, CheckCircle, Clock, Award, BookOpen, Loader2, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from '@/components/ui/sonner';

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();

  const recentNotifications = notifications.slice(0, 5);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'course_assigned':
        return <BookOpen className="h-4 w-4 text-blue-500" />;
      case 'progress_milestone':
        return <Clock className="h-4 w-4 text-green-500" />;
      case 'course_completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'certificate_issued':
        return <Award className="h-4 w-4 text-yellow-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const getNavigationPath = (notification: any) => {
    const { type, data } = notification;
    console.log('🧭 Getting navigation path for:', { type, data });
    
    switch (type) {
      case 'course_assigned':
        if (data?.course_id) {
          const path = `/courses/${data.course_id}`;
          console.log('📍 Course assigned navigation:', path);
          return path;
        }
        console.log('📍 Course assigned fallback to dashboard');
        return '/dashboard';
      case 'course_completed':
        if (data?.course_id) {
          const path = `/courses/${data.course_id}`;
          console.log('📍 Course completed navigation:', path);
          return path;
        }
        console.log('📍 Course completed fallback to dashboard');
        return '/dashboard';
      case 'progress_milestone':
        console.log('📍 Progress milestone navigation: /dashboard');
        return '/dashboard';
      case 'certificate_issued':
        console.log('📍 Certificate issued navigation: /certificates');
        return '/certificates';
      case 'test':
        console.log('📍 Test notification navigation: /dashboard');
        return '/dashboard';
      default:
        console.log('📍 Default navigation: /dashboard');
        return '/dashboard';
    }
  };

  const handleNotificationClick = async (notification: any) => {
    console.log('🔔 Notification clicked:', notification);
    
    try {
      // Mark as read if unread
      if (!notification.is_read) {
        console.log('📖 Marking notification as read');
        setMarkingAsRead(notification.id);
        try {
          await markAsRead(notification.id);
        } catch (error) {
          console.error('❌ Error marking notification as read:', error);
          toast.error('Failed to mark notification as read');
        } finally {
          setMarkingAsRead(null);
        }
      }

      // Navigate to relevant page
      const navigationPath = getNavigationPath(notification);
      if (navigationPath) {
        console.log('🧭 Navigating to:', navigationPath);
        navigate(navigationPath);
      }

      // Close the popover
      console.log('❌ Closing notification popover');
      setOpen(false);
    } catch (error) {
      console.error('❌ Error handling notification click:', error);
      toast.error('Failed to process notification. Please try again.');
    }
  };

  const handleMarkAllAsRead = async () => {
    console.log('📖 Mark all as read clicked');
    setMarkingAllAsRead(true);
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('❌ Error marking all as read:', error);
      toast.error('Failed to mark all notifications as read');
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  if (loading) {
    return (
      <Button variant="ghost" size="sm" className="relative" disabled>
        <Loader2 className="h-5 w-5 animate-spin" />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative hover:bg-muted">
          {unreadCount > 0 ? (
            <Bell className="h-5 w-5 fill-current" />
          ) : (
            <Bell className="h-5 w-5" />
          )}
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-5 w-5 p-0 text-xs flex items-center justify-center min-w-[20px]"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-96 p-0 bg-background border border-border shadow-lg z-[60]" 
        align="end"
        sideOffset={8}
      >
        <div className="p-4 border-b border-border bg-background">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={markingAllAsRead}
                className="text-xs h-auto p-1 hover:bg-muted"
              >
                {markingAllAsRead ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : null}
                Mark all read
              </Button>
            )}
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto bg-background">
          {recentNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications</p>
              <p className="text-xs text-muted-foreground mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="py-2">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-blue-50/50 dark:bg-blue-950/20' : 'bg-background'
                  }`}
                  onClick={() => {
                    console.log('🖱️ Notification item clicked:', notification.id);
                    handleNotificationClick(notification);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium line-clamp-1 ${
                          !notification.is_read ? 'text-foreground' : 'text-muted-foreground'
                        }`}>
                          {notification.title}
                        </p>
                         {!notification.is_read && (
                           <div className="flex items-center gap-1">
                             {markingAsRead === notification.id ? (
                               <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                             ) : (
                               <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
                             )}
                           </div>
                         )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {notifications.length > 5 && (
          <div className="p-3 border-t border-border bg-muted/30">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-sm h-auto py-2"
              onClick={() => {
                console.log('📄 View all notifications clicked');
                navigate('/notifications');
                setOpen(false);
              }}
            >
              View all notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
